import type {
  UpdateLedgerAccountMutationOptions,
  UpdateLedgerAccountVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysLedgerAccount } from '../queryKeys';

import { updateLedgerAccount } from './updateLedgerAccount';

/**
 * Custom hook for opening a new effective-dated version of a ledger account.
 *
 * @param options - Additional options for the mutation.
 * @returns An object containing the mutation function and related properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Ledger-Accounts/operation/update_ledger_account_ledger_accounts__ledger_account_id__patch
 */
export const useMutationUpdateLedgerAccount = (
  options?: UpdateLedgerAccountMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ledgerAccountId,
      payload,
    }: UpdateLedgerAccountVariables) =>
      updateLedgerAccount(ledgerAccountId, payload),
    ...options,
    onSuccess(data, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysLedgerAccount.get(variables.ledgerAccountId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysLedgerAccount.getAll(),
      });

      return options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
